import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Radio, FileAudio, Sparkles, Users, Search,
  BarChart3, Settings as SettingsIcon, ShieldCheck, LogOut, Menu, X, Mic2, ChevronRight, Bell, UserCircle2, BookOpen,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Roles that have write/management access
const WRITE_ROLES = ["super_admin", "org_admin", "moderator", "host"];
// Roles that have admin access only
const ADMIN_ROLES = ["super_admin", "org_admin"];
// Roles that can read reports & summaries (all except pure participant)
const READ_AND_WRITE = [...WRITE_ROLES, "viewer", "auditor"];

/**
 * NAV items.
 * `roles` — if defined, only listed roles can see this item.
 * No `roles` = visible to every authenticated user.
 *
 * Participant (peserta) access:
 *   ✓ Dashboard, Calendar               — overview
 *   ✓ Realtime Transcript               — join a live meeting
 *   ✓ Search, Voice Biometrics          — find & enroll voice
 *   ✗ Meeting CRUD, AI Summary, Reports, User & Role, Settings — management only
 */
const NAV = [
  { section: "Ringkasan", items: [
    { to: "/dashboard",  label: "Dashboard",  icon: LayoutDashboard, id: "dashboard" },
    { to: "/calendar",   label: "Calendar",   icon: CalendarDays,    id: "calendar"  },
  ]},
  { section: "Rapat", items: [
    { to: "/meetings",      label: "Meeting",             icon: FileAudio, id: "meetings",     roles: WRITE_ROLES },
    { to: "/live",          label: "Realtime Transcript", icon: Radio,     id: "live"          },   // all — participant joins here
    { to: "/summaries",     label: "AI Summary",          icon: Sparkles,  id: "summaries",    roles: READ_AND_WRITE },
    { to: "/participants",  label: "Participants",         icon: Users,     id: "participants", roles: WRITE_ROLES },
  ]},
  { section: "Intelijen", items: [
    { to: "/search",           label: "Search",          icon: Search,   id: "search"  },   // all
    { to: "/reports",          label: "Reports",         icon: BarChart3, id: "reports", roles: READ_AND_WRITE },
    { to: "/voice-enrollment", label: "Voice Biometrics", icon: Mic2,    id: "voice"   },   // all — participant enrolls here
    { to: "/knowledge-center", label: "Knowledge Center", icon: BookOpen, id: "knowledge" }, // all — help articles
  ]},
  { section: "Administrasi", items: [
    { to: "/users",    label: "User & Role", icon: ShieldCheck,  id: "users",    roles: ADMIN_ROLES  },
    { to: "/settings", label: "Settings",    icon: SettingsIcon, id: "settings", roles: WRITE_ROLES  },
  ]},
];

const CRUMBS = {
  dashboard: "Dashboard", meetings: "Meeting", live: "Realtime Transcript", summaries: "AI Summary",
  participants: "Participants", calendar: "Calendar", reports: "Reports", search: "Search",
  settings: "Settings", users: "User & Role", "voice-enrollment": "Voice Biometrics",
  profile: "Profile", "change-password": "Change Password", new: "Create Meeting",
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const parts = loc.pathname.split("/").filter(Boolean);

  // Filter nav items based on current user's role
  const userRole = user?.role || "";
  const visibleNav = NAV
    .map((group) => ({
      ...group,
      items: group.items.filter((it) => !it.roles || it.roles.includes(userRole)),
    }))
    .filter((group) => group.items.length > 0);

  const isParticipant = userRole === "participant";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-white border-r border-slate-200/70 flex flex-col transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center px-5 border-b border-slate-200/70">
          <Link to="/dashboard" onClick={() => setOpen(false)}>
            <Logo size={38} />
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-5 no-scrollbar">
          {visibleNav.map((group) => (
            <div key={group.section} className="mb-6">
              <p className="px-3 mb-2 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">{group.section}</p>
              <div className="space-y-0.5">
                {group.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${it.id}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-[background-color,color] duration-200 ${
                        isActive ? "grad-brand text-white shadow-clay" : "text-slate-600 hover:bg-slate-50 hover:text-brand-700"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <it.icon size={17} strokeWidth={2.1} className={isActive ? "text-white" : "text-slate-400 group-hover:text-brand-600"} />
                        <span className="truncate">{it.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200/70">
          {isParticipant ? (
            <div className="rounded-xl bg-gradient-to-br from-violet-50 to-blue-50 border border-violet-100 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Mode Peserta</p>
              <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                Bergabung ke rapat live melalui menu <strong>Realtime Transcript</strong> di atas.
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-4">
              <p className="label-xs">On-Premise</p>
              <p className="mt-2 text-xs text-slate-600 leading-relaxed">AES-256 · TLS · Audit Trail aktif. Data tidak keluar dari infrastruktur Anda.</p>
            </div>
          )}
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 glass border-b flex items-center gap-4 px-4 sm:px-8" data-testid="app-topbar">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="hidden sm:flex items-center gap-1.5 text-sm text-slate-500" data-testid="breadcrumb">
            <Link to="/dashboard" className="hover:text-brand-600 transition-colors">VECTRA</Link>
            {parts.map((p, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <ChevronRight size={14} className="text-slate-300" />
                <span className={i === parts.length - 1 ? "text-slate-900 font-medium" : ""}>
                  {CRUMBS[p] || (p.length > 12 ? "Detail" : p)}
                </span>
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/search")} data-testid="topbar-search-btn" className="text-slate-500">
              <Search size={17} />
            </Button>
            <Button variant="ghost" size="sm" className="relative text-slate-500" data-testid="topbar-notification-btn">
              <Bell size={17} />
              <span className="absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full bg-cyanx" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
                  <span className="grid place-items-center h-8 w-8 rounded-full grad-brand text-white text-xs font-semibold">
                    {(user?.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="hidden sm:block text-left leading-tight">
                    <span className="block text-sm font-medium text-slate-800">{user?.name}</span>
                    <span className="block text-[11px] text-slate-500">{user?.role_label}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">
                  <UserCircle2 size={15} className="mr-2" /> Profil Saya
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/change-password")} data-testid="menu-change-password">
                  <ShieldCheck size={15} className="mr-2" /> Ubah Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => { await logout(); nav("/login"); }}
                  className="text-red-600"
                  data-testid="menu-logout"
                >
                  <LogOut size={15} className="mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 sm:px-8 py-8 max-w-[1600px]" data-testid="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
